/*
  ss-pdp-hero.js — Big Set PDP hero, phase 1.

  Three custom elements, none of which touch the cart or the variant fetch.
  Adding to cart is the theme's <product-form>; variant re-rendering is the
  theme's <product-info>. This file only owns gallery navigation, the tab
  strip, and keeping the upsell cards in step with the main colour choice.
*/

/* ── gallery ──────────────────────────────────────────────────────────────
   One visible-position index drives stage, thumbs, arrows and lightbox.
   Media can be tagged in alt text as color:Blue / color:Pine / color:shared
   (colour: also accepted). Untagged media is treated as shared. On swatch
   change the gallery shows only shared + matching-color slides.
*/
if (!customElements.get('ss-pdp-gallery')) {
  customElements.define(
    'ss-pdp-gallery',
    class SsPdpGallery extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-ss-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-ss-thumb]'));
        this.railItems = Array.from(this.querySelectorAll('[data-ss-rail-item]'));
        this.rail = this.querySelector('[data-ss-rail]');
        this.railWrap = this.querySelector('[data-ss-rail-wrap]');
        this.stage = this.querySelector('[data-ss-stage]');
        this.lightbox = this.querySelector('[data-ss-lightbox]');
        this.lightboxImage = this.querySelector('[data-ss-lightbox-image]');
        this.lightboxCount = this.querySelector('[data-ss-lightbox-count]');
        this.absoluteIndex = 0;
        this.visibleIndexes = [];
        this.activeColour = this.normalizeColour(this.dataset.ssSelectedColour || '');

        if (!this.slides.length) return;

        this.slides.forEach((slide, i) => {
          const fromData = slide.dataset.ssColour;
          const fromAlt = this.parseColourTag(slide.querySelector('img')?.alt || '');
          slide.dataset.ssColour = this.normalizeColour(fromData || fromAlt || 'shared');
          const thumb = this.thumbs[i];
          if (thumb) thumb.dataset.ssColour = slide.dataset.ssColour;
        });

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => {
            const absolute = Number(thumb.dataset.index);
            const visiblePos = this.visibleIndexes.indexOf(absolute);
            if (visiblePos > -1) this.selectVisible(visiblePos);
          })
        );

        this.querySelectorAll('[data-ss-prev]').forEach((button) =>
          button.addEventListener('click', () => this.selectVisible(this.visiblePos - 1))
        );
        this.querySelectorAll('[data-ss-next]').forEach((button) =>
          button.addEventListener('click', () => this.selectVisible(this.visiblePos + 1))
        );

        this.querySelector('[data-ss-rail-prev]')?.addEventListener('click', () => this.scrollRail(-1));
        this.querySelector('[data-ss-rail-next]')?.addEventListener('click', () => this.scrollRail(1));

        this.querySelector('[data-ss-open-lightbox]')?.addEventListener('click', () => this.openLightbox());
        this.querySelector('[data-ss-close-lightbox]')?.addEventListener('click', () => this.closeLightbox());
        this.lightbox?.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.closeLightbox();
        });

        this.onKeydown = this.handleKeydown.bind(this);

        if (this.rail) {
          this.rail.addEventListener('scroll', () => this.updateRailFades(), { passive: true });
        }

        if (this.stage) {
          this.bindStageSwipe(this.stage);
        }

        if (this.lightbox) {
          this.bindStageSwipe(this.lightbox, { requireOpen: true });
        }

        if (typeof subscribe === 'function') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.section) return;
            const colour = this.colourFromVariant(data.variant);
            this.applyColourFilter(colour, data.variant?.featured_media?.id);
          });
        }

        this.applyColourFilter(this.activeColour, this.dataset.featuredMediaId);
      }

      disconnectedCallback() {
        this.variantUnsubscriber?.();
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('ss-lightbox-open');
      }

      /*
        Horizontal swipe on the main stage (and lightbox when open). Vertical
        scrolls are ignored so the page still scrolls normally.
      */
      bindStageSwipe(target, { requireOpen = false } = {}) {
        const threshold = 42;
        let startX = 0;
        let startY = 0;
        let tracking = false;

        target.addEventListener(
          'touchstart',
          (event) => {
            if (requireOpen && !this.isLightboxOpen) return;
            if (event.touches.length !== 1) return;
            tracking = true;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            if (target === this.stage) this.stage.classList.add('is-touching');
          },
          { passive: true }
        );

        target.addEventListener(
          'touchend',
          (event) => {
            if (target === this.stage) this.stage.classList.remove('is-touching');
            if (!tracking) return;
            tracking = false;
            if (requireOpen && !this.isLightboxOpen) return;

            const touch = event.changedTouches[0];
            if (!touch) return;
            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) return;

            if (dx < 0) this.selectVisible(this.visiblePos + 1);
            else this.selectVisible(this.visiblePos - 1);
          },
          { passive: true }
        );

        target.addEventListener(
          'touchcancel',
          () => {
            tracking = false;
            if (target === this.stage) this.stage.classList.remove('is-touching');
          },
          { passive: true }
        );
      }

      /* color:Blue | …  or  colour:Sand Dune | …  — untagged ⇒ shared */
      parseColourTag(alt) {
        const match = String(alt).match(/colou?r:\s*([^|]+)/i);
        if (!match) return 'shared';
        return match[1].trim();
      }

      normalizeColour(value) {
        return String(value || 'shared')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');
      }

      colourFromVariant(variant) {
        if (!variant) return this.activeColour;
        return this.normalizeColour(variant.option1 || variant.options?.[0] || '');
      }

      slideMatchesColour(slide, colour) {
        const tag = this.normalizeColour(slide.dataset.ssColour || 'shared');
        return tag === 'shared' || tag === colour;
      }

      applyColourFilter(colour, preferredMediaId) {
        this.activeColour = this.normalizeColour(colour || this.activeColour);
        this.visibleIndexes = this.slides
          .map((slide, i) => i)
          .filter((i) => this.slideMatchesColour(this.slides[i], this.activeColour));

        if (!this.visibleIndexes.length) {
          this.visibleIndexes = this.slides.map((_, i) => i);
        }

        this.slides.forEach((slide, i) => {
          const inSet = this.visibleIndexes.includes(i);
          slide.classList.toggle('ss-gallery__slide--colour-hidden', !inSet);
          if (this.railItems[i]) this.railItems[i].hidden = !inSet;
          else if (this.thumbs[i]?.closest('[data-ss-rail-item]')) {
            this.thumbs[i].closest('[data-ss-rail-item]').hidden = !inSet;
          } else if (this.thumbs[i]?.parentElement) {
            this.thumbs[i].parentElement.hidden = !inSet;
          }
        });

        let nextAbsolute = this.visibleIndexes[0];
        if (preferredMediaId) {
          const featured = this.slides.findIndex(
            (slide) => slide.dataset.mediaId === String(preferredMediaId)
          );
          if (featured > -1 && this.visibleIndexes.includes(featured)) nextAbsolute = featured;
        }

        this.selectAbsolute(nextAbsolute, { force: true });
        this.updateRailFades();
      }

      get visiblePos() {
        const pos = this.visibleIndexes.indexOf(this.absoluteIndex);
        return pos > -1 ? pos : 0;
      }

      selectVisible(nextVisible) {
        if (!this.visibleIndexes.length) return;
        const clamped = Math.max(0, Math.min(nextVisible, this.visibleIndexes.length - 1));
        this.selectAbsolute(this.visibleIndexes[clamped]);
      }

      selectAbsolute(absoluteIndex, { force = false } = {}) {
        if (!this.visibleIndexes.includes(absoluteIndex)) return;
        if (!force && absoluteIndex === this.absoluteIndex) {
          this.syncActiveClasses();
          this.updateControls();
          return;
        }

        this.pauseMedia(this.slides[this.absoluteIndex]);
        this.absoluteIndex = absoluteIndex;
        this.syncActiveClasses();
        this.thumbs[absoluteIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this.updateControls();
        if (this.isLightboxOpen) this.renderLightbox();
      }

      syncActiveClasses() {
        this.slides.forEach((slide, i) => {
          const active = i === this.absoluteIndex;
          const inSet = this.visibleIndexes.includes(i);
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          if (!inSet) slide.hidden = true;
        });
        this.thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === this.absoluteIndex);
        });
      }

      selectMediaId(mediaId) {
        if (!mediaId) return;
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) this.selectAbsolute(index, { force: true });
      }

      pauseMedia(slide) {
        slide?.querySelector('video')?.pause();
      }

      updateControls() {
        const atStart = this.visiblePos <= 0;
        const atEnd = this.visiblePos >= this.visibleIndexes.length - 1;
        this.querySelectorAll('[data-ss-prev]').forEach((button) => {
          button.disabled = atStart;
        });
        this.querySelectorAll('[data-ss-next]').forEach((button) => {
          button.disabled = atEnd;
        });
        this.updateRailFades();
      }

      /*
        A soft fade at whichever edge has more content, rather than a native
        scrollbar sitting over the photography.
      */
      updateRailFades() {
        if (!this.rail || !this.railWrap) return;
        const vertical = getComputedStyle(this.rail).flexDirection === 'column';
        const scrolled = vertical ? this.rail.scrollTop : this.rail.scrollLeft;
        const total = vertical ? this.rail.scrollHeight : this.rail.scrollWidth;
        const visible = vertical ? this.rail.clientHeight : this.rail.clientWidth;

        this.railWrap.classList.toggle('is-faded-top', scrolled > 2);
        this.railWrap.classList.toggle('is-faded-bottom', scrolled + visible < total - 2);
      }

      scrollRail(direction) {
        if (!this.rail) return;
        const step = this.rail.clientHeight * 0.6;
        this.rail.scrollBy({ top: step * direction, behavior: 'smooth' });
      }

      get isLightboxOpen() {
        return this.lightbox && !this.lightbox.hidden;
      }

      openLightbox() {
        if (!this.lightbox) return;
        this.renderLightbox();
        this.lightbox.hidden = false;
        document.body.classList.add('ss-lightbox-open');
        document.addEventListener('keydown', this.onKeydown);
        this.querySelector('[data-ss-close-lightbox]')?.focus();
      }

      closeLightbox() {
        if (!this.lightbox) return;
        this.lightbox.hidden = true;
        document.body.classList.remove('ss-lightbox-open');
        document.removeEventListener('keydown', this.onKeydown);
        this.querySelector('[data-ss-open-lightbox]')?.focus();
      }

      /*
        The viewer shows a larger source than the inline stage rather than the
        same file scaled up. Video frames fall back to their poster, since the
        viewer has no play affordance yet.
      */
      renderLightbox() {
        const slide = this.slides[this.absoluteIndex];
        const image = slide?.querySelector('img');
        if (this.lightboxImage && image) {
          this.lightboxImage.src = image.dataset.ssFullSrc || image.currentSrc || image.src;
          this.lightboxImage.alt = image.alt || '';
        }
        if (this.lightboxCount) {
          this.lightboxCount.textContent = `${this.visiblePos + 1} / ${this.visibleIndexes.length}`;
        }
      }

      handleKeydown(event) {
        if (!this.isLightboxOpen) return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.selectVisible(this.visiblePos - 1);
        if (event.key === 'ArrowRight') this.selectVisible(this.visiblePos + 1);
      }
    }
  );
}

/* ── buy-column tabs ─────────────────────────────────────────────────── */
if (!customElements.get('ss-pdp-tabs')) {
  customElements.define(
    'ss-pdp-tabs',
    class SsPdpTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panes = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
        });
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active) {
            tab.removeAttribute('tabindex');
          } else {
            tab.setAttribute('tabindex', '-1');
          }
        });
        this.panes.forEach((pane, i) => {
          pane.hidden = i !== index;
          pane.classList.toggle('is-active', i === index);
        });
      }

      handleKeydown(event, index) {
        const last = this.tabs.length - 1;
        let next = null;

        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;

        event.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      }
    }
  );
}

/* ── linked upsell colour pickers ─────────────────────────────────────────
   Every add-on carries the same single Color option with the same seven
   values in the same order as the main product, so matching is a direct
   lookup by value with no translation table and no missing-colour case.
   A card that is overridden by hand stops following the main picker.
*/
if (!customElements.get('ss-pdp-upsells')) {
  customElements.define(
    'ss-pdp-upsells',
    class SsPdpUpsells extends HTMLElement {
      connectedCallback() {
        this.colours = this.readJson(this.querySelector('[data-ss-swatch-colours]')) || {};
        this.cards = Array.from(this.querySelectorAll('[data-ss-upsell-card]')).map((element) => ({
          element,
          variants: this.readJson(element.querySelector('[data-ss-upsell-variants]')) || {},
          image: element.querySelector('[data-ss-upsell-image]'),
          price: element.querySelector('[data-ss-upsell-price]'),
          chip: element.querySelector('[data-ss-upsell-chip]'),
          name: element.querySelector('[data-ss-upsell-colour-name]'),
          picker: element.querySelector('[data-ss-upsell-picker]'),
          pickerRow: element.querySelector('[data-ss-upsell-picker-row]'),
          soldOut: element.querySelector('[data-ss-upsell-sold-out]'),
          preorderInput: element.querySelector('[data-ss-upsell-preorder-note]'),
          input: element.querySelector('[data-ss-upsell-variant-input]'),
          form: element.querySelector('[data-ss-upsell-form]'),
          button: element.querySelector('[data-ss-upsell-add]'),
          label: element.querySelector('[data-ss-upsell-add-label]'),
          lineKey: null,
          unlinked: false,
        }));

        this.cards.forEach((card) => this.bindCard(card));
        this.apply(this.dataset.selectedValue);

        this.onMainSwatchChange = (event) => {
          const input = event.target.closest?.('input[type="radio"]');
          if (!input?.value || !input.closest('.ss-hero__swatch-list')) return;
          this.apply(input.value);
        };
        document.querySelector('.ss-hero__swatch-list')?.addEventListener('change', this.onMainSwatchChange);

        if (typeof subscribe === 'function') {
          this.unsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.section) return;
            const value = data.variant?.option1;
            if (value) this.apply(value);
          });
        }
      }

      disconnectedCallback() {
        this.unsubscriber?.();
        document.querySelector('.ss-hero__swatch-list')?.removeEventListener('change', this.onMainSwatchChange);
      }

      readJson(node) {
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('ss-pdp-hero: could not parse upsell data', error);
          return null;
        }
      }

      colourFor(value) {
        if (!value) return 'transparent';
        const mapped = this.colours[value];
        if (mapped) return mapped;
        const wanted = String(value).toLowerCase().trim();
        const key = Object.keys(this.colours || {}).find((name) => name.toLowerCase().trim() === wanted);
        if (key && this.colours[key]) return this.colours[key];
        if (wanted === 'sea mist') return '#CBD6F1';
        return 'transparent';
      }

      variantFor(card, value) {
        if (!card?.variants || !value) return null;
        if (card.variants[value]) return card.variants[value];
        const wanted = String(value).toLowerCase().trim();
        const key = Object.keys(card.variants).find((name) => name.toLowerCase().trim() === wanted);
        return key ? card.variants[key] : null;
      }

      bindCard(card) {
        card.element.querySelector('[data-ss-upsell-change]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!card.picker) return;
          card.picker.hidden = !card.picker.hidden;
          this.syncStatus(card);
        });

        card.picker?.querySelectorAll('[data-ss-upsell-pick]').forEach((swatch) => {
          const value = swatch.dataset.ssUpsellPick;
          swatch.style.setProperty('--ss-pick-colour', this.colourFor(value));
          const pickVariant = this.variantFor(card, value);
          swatch.classList.toggle('is-sold-out', !pickVariant || !pickVariant.available);
          swatch.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            card.unlinked = true;
            card.element.classList.add('is-unlinked');
            card.picker.hidden = true;
            this.applyToCard(card, value);
          });
        });

        card.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.toggleCartLine(card);
        });
      }

      setAddedState(card, added) {
        card.element.classList.toggle('is-added', added);
        card.button?.classList.toggle('is-added', added);
        if (card.label && card.button) {
          const raw = added ? card.button.dataset.labelAdded : card.button.dataset.labelAdd;
          const fallback = added ? 'Added' : '+ Add';
          const label = raw && !/translation missing/i.test(raw) ? raw : fallback;
          card.label.textContent = label;
        }
      }

      async toggleCartLine(card) {
        if (!card.button || card.button.disabled || card.button.getAttribute('aria-disabled') === 'true') return;

        const spinner = card.button.querySelector('.loading__spinner');
        card.button.setAttribute('aria-disabled', 'true');
        card.button.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          if (card.lineKey) {
            await this.changeLine(card, 0);
            card.lineKey = null;
            this.setAddedState(card, false);
          } else {
            const item = await this.addLine(card);
            card.lineKey = item?.key || null;
            this.setAddedState(card, Boolean(card.lineKey || item?.id));
            if (!card.lineKey && item?.id) {
              /* Some responses omit key; still treat as added. */
              card.lineKey = String(item.key || item.id);
            }
          }
        } catch (error) {
          console.error('ss-pdp-hero: upsell cart update failed', error);
        } finally {
          card.button.classList.remove('loading');
          card.button.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }

      async addLine(card) {
        const formData = new FormData(card.form);
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (cart?.getSectionsToRender) {
          const sectionIds = cart.getSectionsToRender().map((section) => section.id).filter(Boolean);
          if (sectionIds.length) {
            formData.append('sections', sectionIds.join(','));
            formData.append('sections_url', window.location.pathname);
          }
        }

        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message || 'Add failed');

        try {
          this.syncCartUi(data, cart);
        } catch (syncError) {
          console.warn('ss-pdp-hero: cart UI sync skipped', syncError);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ss-upsell',
            productVariantId: formData.get('id'),
            cartData: data,
          });
        }
        return data;
      }

      async changeLine(card, quantity) {
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || [];
        const response = await fetch(window.routes?.cart_change_url || `${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: card.lineKey,
            quantity,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message || 'Change failed');

        try {
          this.syncCartUi(data, cart);
        } catch (syncError) {
          console.warn('ss-pdp-hero: cart UI sync skipped', syncError);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ss-upsell',
            productVariantId: card.input?.value,
            cartData: data,
          });
        }
        return data;
      }

      /* Update drawer + bubble HTML without opening the drawer (Whisker-style). */
      syncCartUi(parsedState, cart) {
        if (!cart || !parsedState?.sections || !cart.getSectionsToRender) return;

        cart.getSectionsToRender().forEach((section) => {
          const html = parsedState.sections[section.id];
          if (!html) return;

          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;

          try {
            if (cart.getSectionInnerHTML) {
              const inner = cart.getSectionInnerHTML(html, section.selector);
              if (inner) sectionElement.innerHTML = inner;
            }
          } catch (error) {
            /* Section HTML shape can differ; skip rather than fail the add. */
          }
        });

        cart.querySelector?.('.drawer__inner.is-empty')?.classList.remove('is-empty');
        cart.classList?.remove?.('is-empty');
      }

      apply(value) {
        if (!value) return;
        this.dataset.selectedValue = value;
        this.cards.forEach((card) => {
          card.unlinked = false;
          card.element.classList.remove('is-unlinked');
          if (card.picker) card.picker.hidden = true;
          this.applyToCard(card, value);
        });
      }

      currentCardValue(card) {
        return (
          card.picker?.querySelector('[aria-pressed="true"]')?.dataset.ssUpsellPick ||
          card.name?.textContent?.trim() ||
          ''
        );
      }

      syncStatus(card, variant) {
        const resolved = variant || this.variantFor(card, this.currentCardValue(card));
        const soldOut = !resolved || !resolved.available;
        const note = !soldOut && resolved?.preorderNote ? resolved.preorderNote : '';

        if (card.soldOut) {
          if (soldOut) {
            card.soldOut.textContent = card.soldOut.dataset.labelSoldOut || 'Sold out';
            card.soldOut.classList.remove('is-preorder');
            card.soldOut.hidden = false;
          } else if (note) {
            card.soldOut.textContent = note;
            card.soldOut.classList.add('is-preorder');
            card.soldOut.hidden = false;
          } else {
            card.soldOut.textContent = '';
            card.soldOut.classList.remove('is-preorder');
            card.soldOut.hidden = true;
          }
        }

        if (card.preorderInput) card.preorderInput.value = note;

        const pickerOpen = Boolean(card.picker && !card.picker.hidden);
        if (card.pickerRow) card.pickerRow.hidden = !pickerOpen;
      }

      applyToCard(card, value) {
        const variant = this.variantFor(card, value);
        const colour = this.colourFor(value);
        const soldOut = !variant || !variant.available;

        if (card.name) card.name.textContent = value;
        if (card.chip) card.chip.style.setProperty('--ss-chip-colour', colour);

        card.picker?.querySelectorAll('[data-ss-upsell-pick]').forEach((swatch) => {
          swatch.setAttribute('aria-pressed', swatch.dataset.ssUpsellPick === value ? 'true' : 'false');
        });

        this.syncStatus(card, variant);

        if (!variant) {
          if (card.button && !card.lineKey) card.button.disabled = true;
          return;
        }

        if (card.input) card.input.value = variant.id;
        if (card.price) card.price.textContent = variant.price;
        if (card.image && variant.image) card.image.src = variant.image;
        if (card.button && !card.lineKey) card.button.disabled = soldOut;
      }
    }
  );
}

/* ── ATC + preorder note ────────────────────────────────────────────────
   Sold out is read from the selected swatch (visually-disabled), not from
   selected_or_first_available_variant — that drop falls back to an in-stock
   colour, which left Sea Mist on Add to cart. Live preorder still needs
   the metafield AND an available variant (Continue selling on).
*/
(() => {
  const sectionRoot = document.querySelector('product-info.ss-hero');
  const wrapper = document.querySelector('[data-ss-preorder]');
  const productForm = (wrapper && wrapper.closest('product-form')) || sectionRoot?.querySelector('product-form');
  const submitButton =
    (wrapper && document.getElementById('ProductSubmitButton-' + wrapper.dataset.sectionId)) ||
    document.querySelector('[data-ss-atc]');
  if (!submitButton && !wrapper) return;

  let notes = {};
  if (wrapper) {
    try {
      notes = JSON.parse(wrapper.getAttribute('data-notes') || '{}');
    } catch (e) {
      notes = {};
    }
  }

  const noteP = wrapper ? wrapper.querySelector('.ss-hero__preorder-note') : null;
  const defaultText = wrapper?.dataset.defaultLabel || window.variantStrings?.addToCart || 'Add to cart';
  const preorderText = wrapper?.dataset.preorderLabel || 'Pre-order Now';
  const soldOutText = window.variantStrings?.soldOut || 'Sold out';
  const preorderInput = productForm ? productForm.querySelector('.preorder-note-property') : null;
  const shipsNoteEl = () => document.querySelector('[data-ss-ships-note]');

  const setShipsNote = (text, isPreorder = false) => {
    const el = shipsNoteEl();
    if (!el) return;
    el.replaceChildren();
    el.classList.remove('is-preorder');
    if (!text) {
      el.hidden = true;
      return;
    }
    el.append(document.createTextNode(' – '));
    const label = document.createElement('span');
    label.className = 'ss-hero__ships-note-text';
    if (isPreorder) label.classList.add('is-preorder');
    label.textContent = text;
    el.append(label);
    el.hidden = false;
  };

  const noteFor = (id) => {
    if (id == null || id === '') return '';
    return notes[id] || notes[String(id)] || '';
  };

  const selectedSwatchAvailable = () => {
    const checked = document.querySelector('.ss-hero__swatch-list input[type="radio"]:checked');
    if (!checked) return true;
    return !checked.classList.contains('visually-disabled');
  };

  const setButton = (disabled, label) => {
    if (!submitButton) return;
    const span = submitButton.querySelector('span:not(.loading__spinner)');
    if (disabled) {
      submitButton.disabled = true;
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-disabled', 'true');
    } else {
      submitButton.disabled = false;
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
    }
    if (span && label) span.textContent = label;
  };

  const update = () => {
    const input = productForm ? productForm.querySelector('.product-variant-id') : null;
    const note = input ? noteFor(input.value) : '';
    const optionAvailable = selectedSwatchAvailable();
    const canPreorder = Boolean(note) && optionAvailable;

    const defaultShips = shipsNoteEl()?.getAttribute('data-default-note') || '';

    if (canPreorder) {
      if (noteP) noteP.textContent = note;
      if (wrapper) wrapper.hidden = true;
      setShipsNote(note, true);
      setButton(false, preorderText);
      if (input) input.disabled = false;
      if (preorderInput) preorderInput.value = note;
    } else if (!optionAvailable) {
      if (wrapper) wrapper.hidden = true;
      if (noteP) noteP.textContent = '';
      setShipsNote(defaultShips);
      setButton(true, soldOutText);
      if (input) input.disabled = true;
      if (preorderInput) preorderInput.value = '';
      productForm?.handleErrorMessage?.();
    } else {
      if (wrapper) wrapper.hidden = true;
      if (noteP) noteP.textContent = '';
      setShipsNote(defaultShips);
      setButton(false, defaultText);
      if (input) input.disabled = false;
      if (preorderInput) preorderInput.value = '';
    }
  };

  update();
  document.querySelector('.ss-hero__swatch-list')?.addEventListener('change', () => {
    update();
    setTimeout(update, 150);
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(update));
  }
})();

/* ── mobile fixed cart dock ─────────────────────────────────────────────
   Shows only after the in-column Add to cart has scrolled above the viewport
   (user has passed it). Hidden while ATC is still on screen or still below
   the fold. Desktop never uses it.
*/
(() => {
  const dock = document.querySelector('[data-ss-mobile-dock]');
  const atc = document.querySelector('[data-ss-atc]');
  if (!dock || !atc || !('IntersectionObserver' in window)) return;

  const dockButton = dock.querySelector('button.ss-hero__mobile-dock-atc, button[type="submit"]');
  const dockSwatches = Array.from(dock.querySelectorAll('[data-ss-mobile-swatch]'));
  const mq = window.matchMedia('(max-width: 999px)');

  const syncButton = () => {
    if (!dockButton) return;
    dockButton.disabled = atc.disabled;
    const label = atc.querySelector('span:not(.loading__spinner)');
    const dockLabel = dockButton.querySelector('span');
    if (label && dockLabel) dockLabel.textContent = label.textContent.trim();
  };

  const syncSwatches = () => {
    const selected = document.querySelector('.ss-hero__swatch-list input[type="radio"]:checked');
    const value = selected?.value;
    dockSwatches.forEach((swatch) => {
      const on = swatch.dataset.value === value;
      swatch.classList.toggle('is-selected', on);
      swatch.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  dockSwatches.forEach((swatch) => {
    swatch.addEventListener('click', () => {
      const match = document.querySelector(
        `.ss-hero__swatch-list input[type="radio"][value="${CSS.escape(swatch.dataset.value)}"]`
      );
      if (match && !match.checked) {
        match.click();
      }
      syncSwatches();
    });
  });

  const setDock = (show) => {
    if (!mq.matches) {
      dock.hidden = true;
      document.body.classList.remove('ss-mobile-dock-open');
      return;
    }
    dock.hidden = !show;
    document.body.classList.toggle('ss-mobile-dock-open', show);
  };

  const shouldShowDock = () => mq.matches && atc.getBoundingClientRect().top < 0;

  const observer = new IntersectionObserver(() => setDock(shouldShowDock()), { threshold: 0 });

  const onScroll = () => setDock(shouldShowDock());

  const start = () => {
    syncButton();
    syncSwatches();
    observer.disconnect();
    window.removeEventListener('scroll', onScroll);
    if (mq.matches) {
      observer.observe(atc);
      window.addEventListener('scroll', onScroll, { passive: true });
      setDock(shouldShowDock());
    } else {
      setDock(false);
    }
  };

  start();
  mq.addEventListener?.('change', start);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(() => {
        syncButton();
        syncSwatches();
      });
    });
  }

  const mo = new MutationObserver(() => {
    syncButton();
    syncSwatches();
  });
  mo.observe(atc, { attributes: true, childList: true, subtree: true });
  document.querySelector('.ss-hero__swatch-list')?.addEventListener('change', syncSwatches);
})();

/* ── Okendo review text trim (one-shot, no write-loop) ──────────────────
   Trims rating/label whitespace so CSS can render (4.8/5.0) 852 Reviews.
   Observes until Okendo mounts, then disconnects — never rewrites on its
   own mutations.
*/
(() => {
  const trimRating = () => {
    const root = document.querySelector('.ss-hero__rating');
    if (!root) return false;

    let ready = false;
    root.querySelectorAll('.oke-sr-rating').forEach((el) => {
      ready = true;
      if (el.dataset.ssTrimmed === '1') return;
      const trimmed = (el.textContent || '').replace(/\s+/g, '').trim();
      if (trimmed) el.textContent = trimmed;
      el.dataset.ssTrimmed = '1';
    });
    root.querySelectorAll('.oke-sr-label-text').forEach((el) => {
      if (el.dataset.ssTrimmed === '1') return;
      el.textContent = (el.textContent || '').replace(/\s+/g, ' ').trim() || 'Reviews';
      el.dataset.ssTrimmed = '1';
    });
    root.querySelectorAll('.oke-sr-count-number').forEach((el) => {
      if (el.dataset.ssTrimmed === '1') return;
      el.textContent = (el.textContent || '').replace(/\D/g, '') || el.textContent;
      el.dataset.ssTrimmed = '1';
    });
    return ready;
  };

  if (trimRating()) return;

  const root = document.querySelector('.ss-hero__rating');
  if (!root) return;

  const mo = new MutationObserver(() => {
    if (trimRating()) mo.disconnect();
  });
  mo.observe(root, { childList: true, subtree: true });
  setTimeout(() => mo.disconnect(), 8000);
})();